import {useReducer} from 'react';
import {produce, current as currentFn} from 'immer';
import {ObjectStrKeys, PtrnStateValue} from '../../patronTypes';
import {PtrnStateUtil} from '../../patronUtils';
import {PtrnMaestroDetalleConfig} from './usePtrnEstadoMaestroDetalleTypes';

export class RdcrMstrDetError extends Error {
   constructor({path, msg}: {path?: string[]; msg?: string}) {
      if (path && path.length > 0) {
         super(
            `useRdcrMstrDet: La ruta proporcionada(${path.join(
               '->'
            )}) es invalida, el nodo solicitado es un valor del estado`
         );
         return;
      }
      super(msg || 'useRdcrMstrDet: Sucedio un error desconocido');
   }
}

//#region acciones enum
export enum RdcrMstrDetAccionTipo {
   RESET_STATE = 'RESET_STATE',
   OVERWRITE_STATE = 'OVERWRITE_STATE',
   UPDATE_CAMPO = 'UPDATE_CAMPO',
   UPDATE_MULTI_CAMPO = 'UPDATE_MULTI_CAMPO',
   ADD_DETALLE = 'ADD_DETALLE',
   ADD_MULTI_DETALLE = 'ADD_MULTI_DETALLE',
   REMOVE_DETALLE = 'REMOVE_DETALLE',
   REMOVE_MULTI_DETALLE = 'REMOVE_MULTI_DETALLE',
   SET_ERROR_CAMPO = 'SET_ERROR_CAMPO',
   SET_ERROR_MULTI_CAMPO = 'SET_ERROR_MULTI_CAMPO',
   REMOVE_UPSERT_MULTI = 'REMOVE_UPSERT_MULTI',
   UPDATE_ADD_MULTI = 'UPDATE_ADD_MULTI',
   RM_ADD_UPDT_MULTI = 'RM_ADD_UPDT_MULTI'
}
//#endregion

//#region acciones parametros
export interface RdcrMstrDetCommonParams {
   afterExecActionFn?: (state: any) => void;
   mostrarLogs?: boolean;
}
export interface RdcrMstrDetAccionResetParams extends RdcrMstrDetCommonParams {
   cfg: PtrnMaestroDetalleConfig;
   withValue?: ObjectStrKeys;
}

export interface RdcrMstrDetAccionOverwriteParams extends RdcrMstrDetCommonParams {
   newState: any;
}

export interface RdcrMstrDetAccionUpdtCampoParams extends RdcrMstrDetCommonParams {
   path: string[];
   campoName: string;
   valor: PtrnStateValue;
}

export interface RdcrMstrDetAccionUpdtMultiCampoParams extends RdcrMstrDetCommonParams {
   camposParams: RdcrMstrDetAccionUpdtCampoParams[];
}

export interface RdcrMstrDetAccionAddDetParams extends RdcrMstrDetCommonParams {
   path: string[];
   detalleCfgPrimitivas: PtrnMaestroDetalleConfig;
   withValue?: ObjectStrKeys;
}

export interface RdcrMstrDetAccionAddMultiDetParams extends RdcrMstrDetCommonParams {
   detallesParams: RdcrMstrDetAccionAddDetParams[];
}

export interface RdcrMstrDetAccionRemoveDetParams extends RdcrMstrDetCommonParams {
   path: string[];
   name: string;
}

export interface RdcrMstrDetAccionRemoveMultiDetParams extends RdcrMstrDetCommonParams {
   detallesParams: RdcrMstrDetAccionRemoveDetParams[];
}

export interface RdcrMstrDetAccionSetErrorCampoParams extends RdcrMstrDetCommonParams {
   path: string[];
   campoName: string;
   errores: string[] | null;
}

export interface RdcrMstrDetAccionSetErrorMultiCampoParams extends RdcrMstrDetCommonParams {
   detallesParams: RdcrMstrDetAccionSetErrorCampoParams[];
}

export interface RdcrMstrDetAccionRmUpdtMultiCampoParams extends RdcrMstrDetCommonParams {
   detallesParams: {
      toRemove: RdcrMstrDetAccionRemoveDetParams[];
      toUpdate: RdcrMstrDetAccionUpdtCampoParams[];
   };
}

export interface RdcrMstrDetAccionUpdtAddMultiCampoParams extends RdcrMstrDetCommonParams {
   detallesParams: {
      toAdd: RdcrMstrDetAccionAddDetParams[];
      toUpdate: RdcrMstrDetAccionUpdtCampoParams[];
   };
}

export interface RdcrMstrDetAccionRmAddUpdtMultiCampoParams extends RdcrMstrDetCommonParams {
   detallesParams: {
      toDelete: RdcrMstrDetAccionRemoveDetParams[];
      toAdd: RdcrMstrDetAccionAddDetParams[];
      toUpdate: RdcrMstrDetAccionUpdtCampoParams[];
   };
}
//#endregion

//#region accions tipos
export type RdcrMstrDetAccionType =
   | {type: RdcrMstrDetAccionTipo.RESET_STATE; payload: RdcrMstrDetAccionResetParams}
   | {type: RdcrMstrDetAccionTipo.OVERWRITE_STATE; payload: RdcrMstrDetAccionOverwriteParams}
   | {type: RdcrMstrDetAccionTipo.UPDATE_CAMPO; payload: RdcrMstrDetAccionUpdtCampoParams}
   | {
        type: RdcrMstrDetAccionTipo.UPDATE_MULTI_CAMPO;
        payload: RdcrMstrDetAccionUpdtMultiCampoParams;
     }
   | {type: RdcrMstrDetAccionTipo.ADD_DETALLE; payload: RdcrMstrDetAccionAddDetParams}
   | {type: RdcrMstrDetAccionTipo.ADD_MULTI_DETALLE; payload: RdcrMstrDetAccionAddMultiDetParams}
   | {type: RdcrMstrDetAccionTipo.REMOVE_DETALLE; payload: RdcrMstrDetAccionRemoveDetParams}
   | {
        type: RdcrMstrDetAccionTipo.REMOVE_MULTI_DETALLE;
        payload: RdcrMstrDetAccionRemoveMultiDetParams;
     }
   | {type: RdcrMstrDetAccionTipo.SET_ERROR_CAMPO; payload: RdcrMstrDetAccionSetErrorCampoParams}
   | {
        type: RdcrMstrDetAccionTipo.SET_ERROR_MULTI_CAMPO;
        payload: RdcrMstrDetAccionSetErrorMultiCampoParams;
     }
   | {
        type: RdcrMstrDetAccionTipo.REMOVE_UPSERT_MULTI;
        payload: RdcrMstrDetAccionRmUpdtMultiCampoParams;
     }
   | {
        type: RdcrMstrDetAccionTipo.UPDATE_ADD_MULTI;
        payload: RdcrMstrDetAccionUpdtAddMultiCampoParams;
     }
   | {
        type: RdcrMstrDetAccionTipo.RM_ADD_UPDT_MULTI;
        payload: RdcrMstrDetAccionRmAddUpdtMultiCampoParams;
     };

export interface PtrnStateMaestroDetalle {
   [k: string]: PtrnStateValue | PtrnStateMaestroDetalle;
}
//#endregion

//#region utility functions
const inicializarEstado = (
   rootCfg: PtrnMaestroDetalleConfig,
   withValue?: ObjectStrKeys,
   afterExecActionFn?: (state: PtrnStateMaestroDetalle) => void
) => {
   const initialize = (currCfg: PtrnMaestroDetalleConfig, withValue?: ObjectStrKeys): any => {
      const state: PtrnStateMaestroDetalle = PtrnStateUtil.generatePatronState(
         currCfg.campos,
         withValue
      );

      // inicializar los objetos detalle
      if (currCfg.detalles) {
         for (const detail of currCfg.detalles) {
            state[detail.id] = {}; // iniciar como objeto vacio
            if (withValue && withValue[detail.id]) {
               for (const currWithValueId in withValue[detail.id]) {
                  const currWithValue = withValue[detail.id][currWithValueId];
                  state[detail.id][currWithValueId] = initialize(detail, currWithValue);
               }
            }
         }
      }

      return state;
   };

   return initialize(rootCfg, withValue);
};

function deleteDetalle(
   draft: PtrnStateMaestroDetalle,
   input: RdcrMstrDetAccionRemoveDetParams,
   omitirPasosExtra?: boolean
) {
   const {name, path, afterExecActionFn} = input;
   try {
      let currNode = draft;
      // Recorremos `path` para llegar al objeto anidado que se desea procesar
      path.forEach((p) => {
         //TODO validar q no permita ingresar a un nodo valor, solo a los detalles, lanzar error con RdcrMstrDetError
         //@ts-ignore la siguiente linea es valida debido a la verificacion el if
         currNode = currNode[p];
      });
      // eliminamos el detalle del nodo encontrado
      delete currNode[name];

      // ejecutar funcion despues de modificacion si fue proporcionada
      if (!omitirPasosExtra && afterExecActionFn) {
         afterExecActionFn(currentFn(draft));
      }
   } catch (error) {
      console.log('%cERROR:useRdcrMstrDet->deleteDetalle', 'color:rgb(204, 0, 0);');
      console.log(error);
      console.log('name', name);
      console.log('path', path);
   }
}

function updateCampo(
   draft: PtrnStateMaestroDetalle,
   input: RdcrMstrDetAccionUpdtCampoParams,
   omitirPasosExtra?: boolean
) {
   const {path, campoName, valor, afterExecActionFn} = input;
   try {
      let currNode = draft;
      // Recorremos `path` para llegar al objeto anidado que se desea procesar
      path.forEach((p) => {
         //TODO validar q no permita ingresar a un nodo valor, solo a los detalles, lanzar error con RdcrMstrDetError
         //@ts-ignore la siguiente linea es valida debido a la verificacion el if
         currNode = currNode[p];
      });

      // Actualizar valor del nodo
      currNode[campoName] = valor;

      // ejecutar funcion despues de modificacion si fue proporcionada
      if (!omitirPasosExtra && afterExecActionFn) {
         afterExecActionFn(currentFn(draft));
      }
   } catch (error) {
      console.log('%cERROR:useRdcrMstrDet->updateCampo', 'color:rgb(204, 0, 0);');
      console.log(error);
      console.log('path', path);
      console.log('campoName', campoName);
      console.log('valor', valor);
   }
}

function addDetalle(
   draft: PtrnStateMaestroDetalle,
   input: RdcrMstrDetAccionAddDetParams,
   omitirPasosExtra?: boolean
) {
   const {path, detalleCfgPrimitivas, withValue, afterExecActionFn} = input;
   try {
      let currNode = draft;
      // Recorremos `path` para llegar al objeto anidado que se desea procesar
      path.forEach((p) => {
         //TODO validar q no permita ingresar a un nodo valor, solo a los detalles, lanzar error con RdcrMstrDetError
         //@ts-ignore
         currNode = currNode[p];
      });
      // Agregar nuevo objeto detalle inicializando con valores definidos en `detalleCfgPrimitivas` y `withValue`

      currNode[detalleCfgPrimitivas.getId(withValue)] = inicializarEstado(
         detalleCfgPrimitivas,
         withValue
      );

      // ejecutar funcion despues de modificacion si fue proporcionada
      if (!omitirPasosExtra && afterExecActionFn) {
         afterExecActionFn(currentFn(draft));
      }
   } catch (error) {
      console.log('%cERROR:useRdcrMstrDet->addDetalle', 'color:rgb(204, 0, 0);');
      console.log(error);
      console.log('path', path);
      console.log('detalleCfgPrimitivas', detalleCfgPrimitivas);
      console.log('withValue', withValue);
   }
}

function setErrorCampo(
   draft: PtrnStateMaestroDetalle,
   input: RdcrMstrDetAccionSetErrorCampoParams,
   omitirPasosExtra?: boolean
) {
   const {path, campoName, errores, afterExecActionFn} = input;
   try {
      let currNode = draft;

      // Recorremos `path` para llegar al objeto anidado que se desea procesar
      path.forEach((p) => {
         //TODO validar q no permita ingresar a un nodo valor, solo a los detalles, lanzar error con RdcrMstrDetError
         //@ts-ignore la siguiente linea es valida debido a la verificacion el if
         currNode = currNode[p];
      });

      // Actualizar valor del nodo
      currNode[campoName].errores = errores;

      // ejecutar funcion despues de modificacion si fue proporcionada
      if (!omitirPasosExtra && afterExecActionFn) {
         afterExecActionFn(currentFn(draft));
      }
   } catch (error) {
      console.log('%cERROR:useRdcrMstrDet->setErrorCampo', 'color:rgb(204, 0, 0);');
      console.log(error);
      console.log('path', path);
      console.log('campoName', campoName);
      console.log('errores', errores);
   }
}
//#endregion

//#region rdcr acciones
//
//
//
function reducer(draft: PtrnStateMaestroDetalle, action: RdcrMstrDetAccionType) {
   if (action.payload.mostrarLogs) {
      console.log('%cuseRdcrMstrDet action.type', 'color: #007acc;', action.type);
      console.log('%cuseRdcrMstrDet action.type', 'color: #007acc;', action.payload);
   }
   switch (action.type) {
      //#region >reset_state
      case RdcrMstrDetAccionTipo.RESET_STATE: {
         const estadoInicial = inicializarEstado(
            action.payload.cfg,
            action.payload.withValue,
            action.payload.afterExecActionFn
         );
         // ejecutar funcion despues de modificacion si fue proporcionada
         if (action.payload.afterExecActionFn) {
            action.payload.afterExecActionFn(estadoInicial);
         }
         return estadoInicial;
      }
      //#endregion

      //#region >overwrite
      case RdcrMstrDetAccionTipo.OVERWRITE_STATE: {
         // ejecutar funcion despues de modificacion si fue proporcionada
         if (action.payload.afterExecActionFn) {
            action.payload.afterExecActionFn(action.payload.newState);
         }
         return action.payload.newState;
      }
      //#endregion

      //#region >update_campo
      case RdcrMstrDetAccionTipo.UPDATE_CAMPO: {
         updateCampo(draft, action.payload);
         break;
      }
      //#endregion

      //#region >updt_multi_campo
      case RdcrMstrDetAccionTipo.UPDATE_MULTI_CAMPO: {
         const {
            payload: {camposParams, afterExecActionFn}
         } = action;

         for (const currDet of camposParams) {
            updateCampo(draft, currDet, true);
         }

         // ejecutar funcion despues de modificacion si fue proporcionada
         if (afterExecActionFn) {
            afterExecActionFn(currentFn(draft));
         }
         break;
      }
      //#endregion

      //#region >add_detalle
      case RdcrMstrDetAccionTipo.ADD_DETALLE: {
         addDetalle(draft, action.payload);
         break;
      }
      //#endregion

      //#region >add_multi_detalle
      case RdcrMstrDetAccionTipo.ADD_MULTI_DETALLE: {
         const {
            payload: {detallesParams, afterExecActionFn}
         } = action;
         for (const currDet of detallesParams) {
            addDetalle(draft, currDet, true);
         }

         // ejecutar funcion despues de modificacion si fue proporcionada
         if (afterExecActionFn) {
            afterExecActionFn(currentFn(draft));
         }
         break;
      }
      //#endregion

      //#region >remove_detalle
      case RdcrMstrDetAccionTipo.REMOVE_DETALLE: {
         deleteDetalle(draft, action.payload);
         break;
      }
      //#endregion

      //#region >remove_multi_detalle
      case RdcrMstrDetAccionTipo.REMOVE_MULTI_DETALLE: {
         const {
            payload: {detallesParams, afterExecActionFn}
         } = action;
         for (const currDet of detallesParams) {
            deleteDetalle(draft, currDet, true);
         }

         // ejecutar funcion despues de modificacion si fue proporcionada
         if (afterExecActionFn) {
            afterExecActionFn(currentFn(draft));
         }
         break;
      }
      //#endregion

      //#region >set_error
      case RdcrMstrDetAccionTipo.SET_ERROR_CAMPO: {
         setErrorCampo(draft, action.payload);
         break;
      }
      //#endregion
      //#region >set_error_multi
      case RdcrMstrDetAccionTipo.SET_ERROR_MULTI_CAMPO: {
         const {
            payload: {detallesParams, afterExecActionFn}
         } = action;
         for (const currDet of detallesParams) {
            setErrorCampo(draft, currDet, true);
         }

         // ejecutar funcion despues de modificacion si fue proporcionada
         if (afterExecActionFn) {
            afterExecActionFn(currentFn(draft));
         }
         break;
      }
      //#endregion
      //#region >remove upsert
      case RdcrMstrDetAccionTipo.REMOVE_UPSERT_MULTI: {
         const {
            payload: {detallesParams, afterExecActionFn}
         } = action;

         //upsert
         for (const currDet of detallesParams.toUpdate) {
            updateCampo(draft, currDet, true);
         }

         //remove
         for (const currDet of detallesParams.toRemove) {
            deleteDetalle(draft, currDet, true);
         }

         // ejecutar funcion despues de modificacion si fue proporcionada
         if (afterExecActionFn) {
            afterExecActionFn(currentFn(draft));
         }
         break;
      }
      //#endregion
      //#region >update add
      case RdcrMstrDetAccionTipo.UPDATE_ADD_MULTI: {
         const {
            payload: {detallesParams, afterExecActionFn}
         } = action;
         //remove
         for (const currDet of detallesParams.toUpdate) {
            updateCampo(draft, currDet, true);
         }

         //add
         for (const currDet of detallesParams.toAdd) {
            addDetalle(draft, currDet, true);
         }

         // ejecutar funcion despues de modificacion si fue proporcionada
         if (afterExecActionFn) {
            afterExecActionFn(currentFn(draft));
         }
         break;
      }
      //#endregion
      //#region >rm add updt
      case RdcrMstrDetAccionTipo.RM_ADD_UPDT_MULTI: {
         //console.group(RdcrMstrDetAccionTipo.RM_ADD_UPDT_MULTI);
         const {
            payload: {detallesParams, afterExecActionFn}
         } = action;
         //remove
         for (const currDet of detallesParams.toDelete) {
            deleteDetalle(draft, currDet, true);
         }

         //add
         for (const currDet of detallesParams.toAdd) {
            addDetalle(draft, currDet, true);
         }

         //upd
         for (const currDet of detallesParams.toUpdate) {
            updateCampo(draft, currDet, true);
         }

         // ejecutar funcion despues de modificacion si fue proporcionada
         if (afterExecActionFn) {
            afterExecActionFn(currentFn(draft));
         }
         //console.groupEnd();
         break;
      }
      //#endregion
      default:
         break;
   }
}
//#endregion

export default function useRdcrMstrDet(cfgPrimitivas: PtrnMaestroDetalleConfig) {
   return useReducer(produce(reducer), cfgPrimitivas, inicializarEstado);
}
